import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon, SearchIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDate, formatNumber } from "@/lib/format";
import { num } from "@/lib/matching";

type AnalyticsOrganisationRow = components["schemas"]["AnalyticsOrganisationRow"];

/** Everything - dates included - comes from the page filter, the same as every other section. */
function buildQuery(apiParams: URLSearchParams) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
  };
}

type ActivityFilter = "all" | "active" | "none";

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

interface OrgEntry {
  row: AnalyticsOrganisationRow;
  total: number;
}

/** The headline sentence: the organisation count, or with a search, how many carry that name. */
export function organisationsHeadline(entries: OrgEntry[], searchTerm: string): string {
  const count = entries.length;

  if (!searchTerm) {
    if (count === 0) return "No organisations.";
    return `${formatNumber(count)} ${plural(count, "organisation", "organisations")}.`;
  }

  if (count === 0) {
    return `No organisation matches "${searchTerm}".`;
  }

  const names = new Set(entries.map((entry) => entry.row.organisationName.trim().toLowerCase()));
  if (names.size === 1) {
    const name = entries[0]!.row.organisationName;
    return `${name}: ${formatNumber(count)} ${plural(count, "organisation", "organisations")} found.`;
  }

  return `"${searchTerm}" matches ${formatNumber(count)} ${plural(count, "organisation", "organisations")}.`;
}

export interface AnalyticsOrganisationsTableProps {
  apiParams: URLSearchParams;
}

/** Section 4: which organisations exist, sorted by name so same-named orgs under different clients sit together.
 * Includes organisations with no documents: the backend returns them deliberately. */
export function AnalyticsOrganisationsTable({ apiParams }: AnalyticsOrganisationsTableProps) {
  const apiParamsKey = apiParams.toString();
  const [rows, setRows] = useState<AnalyticsOrganisationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const searchTerm = debouncedSearch.trim();
  const searchNeedle = searchTerm.toLowerCase();

  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");

  // The dates in force, printed so a screenshot says what period it covers.
  const windowLabel = `${formatDate(apiParams.get("From") ?? "")} to ${formatDate(apiParams.get("To") ?? "")}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/analytics/organisations", {
        params: { query: buildQuery(apiParams) },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setError("Organisations could not be loaded.");
      } else {
        setRows(data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, refreshKey]);

  const allEntries = useMemo<OrgEntry[]>(() => {
    return rows
      .map((row) => ({ row, total: num(row.totalDocuments) }))
      .sort(
        (a, b) =>
          a.row.organisationName.localeCompare(b.row.organisationName, "en", { sensitivity: "base" }) ||
          a.row.clientName.localeCompare(b.row.clientName, "en", { sensitivity: "base" }),
      );
  }, [rows]);

  // Only the search decides the headline; the chip narrows the table without changing the answer.
  const searchedEntries = useMemo(
    () =>
      searchNeedle
        ? allEntries.filter((entry) => entry.row.organisationName.toLowerCase().includes(searchNeedle))
        : allEntries,
    [allEntries, searchNeedle],
  );

  const visibleEntries = useMemo(
    () =>
      searchedEntries.filter((entry) => {
        if (activityFilter === "active" && entry.total === 0) return false;
        if (activityFilter === "none" && entry.total > 0) return false;
        return true;
      }),
    [searchedEntries, activityFilter],
  );

  if (loading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-10 w-72 animate-pulse rounded-md" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load organisations</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  const windowNote = (
    <p className="text-muted-foreground text-sm">Document counts cover {windowLabel}.</p>
  );

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {windowNote}
        <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
          <InfoIcon className="text-muted-foreground size-6" />
          <p className="text-sm font-medium">No organisations under the selected clients</p>
          <p className="text-muted-foreground max-w-md text-sm">
            Check that the selected clients have any organisations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-lg leading-snug font-medium">{organisationsHeadline(searchedEntries, searchTerm)}</p>
        {windowNote}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="relative w-72">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            type="search"
            placeholder="Search organisation name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
            aria-label="Search organisation name"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs font-medium">Show</span>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={activityFilter}
            onValueChange={(value) => {
              if (value) setActivityFilter(value as ActivityFilter);
            }}
          >
            <ToggleGroupItem value="all">Any activity</ToggleGroupItem>
            <ToggleGroupItem value="active">Active</ToggleGroupItem>
            <ToggleGroupItem value="none">No activity</ToggleGroupItem>
          </ToggleGroup>
          <InfoHint label="Activity filter">
            Active means the organisation received at least one document in the selected date range. No
            activity means it received none. An organisation with no activity still exists and is still
            listed.
          </InfoHint>
        </div>
      </div>

      <p className="text-muted-foreground text-xs">
        Showing {formatNumber(visibleEntries.length)} of {formatNumber(allEntries.length)}{" "}
        {plural(allEntries.length, "organisation", "organisations")}, sorted by name.
      </p>

      {visibleEntries.length === 0 ? (
        <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center">
          <InfoIcon className="text-muted-foreground size-6" />
          <p className="text-sm font-medium">No organisations match these filters</p>
          <p className="text-muted-foreground max-w-md text-sm">
            Clear the search box, or set the activity filter back to Any activity.
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organisation</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">
                  <span className="inline-flex flex-col items-end">
                    <span>Documents</span>
                    <span className="text-muted-foreground text-[10px] font-normal">{windowLabel}</span>
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleEntries.map((entry) => (
                <TableRow key={entry.row.organisationId}>
                  <TableCell className="max-w-[240px] truncate font-medium" title={entry.row.organisationName}>
                    <Link to={`/organisations/${entry.row.organisationId}`} className="hover:underline">
                      {entry.row.organisationName}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate" title={entry.row.clientName}>
                    <Link to={`/clients/${entry.row.clientId}`} className="hover:underline">
                      {entry.row.clientName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {formatDate(entry.row.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    {entry.total === 0 ? (
                      <span className="inline-flex flex-col items-end">
                        <span className="tabular-nums">0</span>
                        <span className="text-muted-foreground text-[10px]">No activity</span>
                      </span>
                    ) : (
                      <span className="font-medium tabular-nums">{formatNumber(entry.total)}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Accordion type="single" collapsible className="border-t">
        <AccordionItem value="details" className="border-b-0">
          <AccordionTrigger>Data details</AccordionTrigger>
          <AccordionContent className="space-y-4 text-sm">
            <p className="text-muted-foreground text-xs">
              The same organisations, in the same order, with their ids and the split by document type.
              Counts cover {windowLabel}.
            </p>
            <div className="max-h-96 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Organisation id</TableHead>
                    <TableHead>Client id</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead className="text-right">Bills</TableHead>
                    <TableHead className="text-right">Purchase orders</TableHead>
                    <TableHead className="text-right">Delivery notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                        No organisations match these filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleEntries.map((entry) => (
                      <TableRow key={entry.row.organisationId}>
                        <TableCell className="max-w-48 truncate" title={entry.row.organisationName}>
                          {entry.row.organisationName}
                        </TableCell>
                        <TableCell>
                          <Link to={`/organisations/${entry.row.organisationId}`}>
                            <Badge variant="secondary" className="hover:bg-primary/20 font-mono text-xs">
                              {entry.row.organisationId.slice(0, 8) + "..."}
                            </Badge>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link to={`/clients/${entry.row.clientId}`}>
                            <Badge variant="secondary" className="hover:bg-primary/20 font-mono text-xs">
                              {entry.row.clientId.slice(0, 8) + "..."}
                            </Badge>
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-40 truncate" title={entry.row.companyName ?? undefined}>
                          {entry.row.companyName ?? <span className="text-muted-foreground">None</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(entry.row.bills)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(entry.row.purchaseOrders)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(entry.row.deliveryNotes)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-muted-foreground text-xs">
              Company is the company inbox an organisation belongs to, where it has one.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
